import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const KINDS = ["LANDING", "BLOG", "FAQ", "HELP", "ANNOUNCEMENT", "RELEASE", "PRIVACY", "TERMS"];

function Pages({ kind }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ slug: "", title: "", content: "", kind, published: true });
  const load = async () => { const { data } = await api.get("/admin/cms", { params: { kind } }); setItems(data); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);
  const save = async () => {
    try { if (editing) await api.put(`/admin/cms/${editing.id}`, form); else await api.post("/admin/cms", form); toast.success("Saved"); setOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (p) => { if (window.confirm(`Delete ${p.title}?`)) { await api.delete(`/admin/cms/${p.id}`); load(); } };

  return (
    <div className="space-y-3">
      <div className="flex justify-end"><Button onClick={() => { setEditing(null); setForm({ slug: "", title: "", content: "", kind, published: true }); setOpen(true); }} data-testid={`cms-${kind}-add`}><Plus className="w-4 h-4 mr-1" /> New {kind.toLowerCase()}</Button></div>
      <div className="grid md:grid-cols-2 gap-3">
        {items.map((p) => (
          <Card key={p.id} data-testid={`cms-${kind}-${p.slug}`}>
            <CardContent className="p-4 space-y-1">
              <div className="flex items-center justify-between">
                <div><div className="font-semibold">{p.title}</div><div className="font-mono text-[10px] text-muted-foreground">{p.slug}</div></div>
                <div className="space-x-1"><Button size="sm" variant="ghost" onClick={() => { setEditing(p); setForm(p); setOpen(true); }}><Pencil className="w-4 h-4" /></Button><Button size="sm" variant="ghost" onClick={() => del(p)}><Trash2 className="w-4 h-4" /></Button></div>
              </div>
              <div className="text-xs text-muted-foreground line-clamp-3">{p.content?.slice(0, 220)}</div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <div className="text-center text-muted-foreground py-10 col-span-2">No {kind.toLowerCase()} pages yet.</div>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit page" : "New page"} · {kind}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} disabled={!!editing} /></div>
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label>Content (markdown)</Label><textarea className="w-full border rounded p-2 text-sm font-mono" rows="10" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} /></div>
            <div className="flex items-center gap-2"><Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} /> <Label>Published</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function CMS() {
  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="admin-cms">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black">CMS & website</h1>
        <p className="text-muted-foreground">Landing page copy, blogs, help center, FAQs, announcement banners, release notes, privacy policy and terms.</p>
      </div>
      <Tabs defaultValue="LANDING">
        <TabsList className="flex-wrap h-auto">
          {KINDS.map((k) => <TabsTrigger key={k} value={k} data-testid={`cms-tab-${k}`}>{k.replace("_", " ")}</TabsTrigger>)}
        </TabsList>
        {KINDS.map((k) => <TabsContent key={k} value={k}><Pages kind={k} /></TabsContent>)}
      </Tabs>
    </div>
  );
}
